import {Injectable} from '@angular/core';
import {VexLayoutService} from '@vex/services/vex-layout.service';
import {NavigationItem} from './navigation-item.interface';
import {BehaviorSubject, Observable} from 'rxjs';
import {AuthService} from '../../shared/services/auth.service';
import {EmployeeService} from '../../shared/services/employee.service';
import {Helper} from '../../shared/constants/helper';
import {PermissionConstant} from '../../shared/constants/permission-constant';

@Injectable({
  providedIn: 'root'
})
export class NavigationLoaderService {
  user: any;
  userData: any;
  private readonly _items: BehaviorSubject<NavigationItem[]> =
    new BehaviorSubject<NavigationItem[]>([]);

  get items$(): Observable<NavigationItem[]> {
    return this._items.asObservable();
  }

  constructor(
    private readonly layoutService: VexLayoutService,
    private authService: AuthService,
    private employeeService: EmployeeService
  ) {
    const self = localStorage.getItem('self');
    if (self) {
      this.authService.self().subscribe((data) => {
        if (data && data.id) {
          this.user = data;
          if (this.user && this.user['user_type'] === 'EMPLOYEE') {
            this.checkUserDetails();
          } else {
            this.loadNavigation();
          }
        }
      });
    } else {
      this.loadNavigation();
    }
  }

  loadNavigation(): void {
    this._items.next([
      {
        type: 'subheading',
        label: 'Dashboard',
        availableTo: [PermissionConstant.ALL],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Home',
            route: '/home',
            icon: 'mat:dashboard',
            routerLinkActiveOptions: {exact: true},
            availableTo: [PermissionConstant.ALL],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Generals',
        availableTo: [PermissionConstant.GENERAL],
        is_hidden: false,
        children: [
          {
            type: 'dropdown',
            label: 'Admins',
            icon: 'mat:admin_panel_settings',
            availableTo: [PermissionConstant.ADMIN],
            is_hidden: (this.user && this.user.user_type && this.user.user_type != 'SUPER_ADMIN'),
            children: [
              {
                type: 'link',
                label: 'Add',
                route: '/general/admin/add',
                availableTo: [PermissionConstant.ADD_ADMIN],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'List',
                route: '/general/admin/list',
                availableTo: [PermissionConstant.LIST_ADMIN],
                is_hidden: false
              }
            ]
          },
          {
            type: 'dropdown',
            label: 'Sessions',
            icon: 'mat:access_time',
            availableTo: [PermissionConstant.SESSION],
            is_hidden: false,
            children: [
              {
                type: 'link',
                label: 'Add',
                route: '/general/session/add',
                availableTo: [PermissionConstant.ADD_SESSION],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'List',
                route: '/general/session/list',
                availableTo: [PermissionConstant.LIST_SESSION],
                is_hidden: false
              }
            ]
          },
          {
            type: 'link',
            label: 'Report',
            route: '/general/reports',
            icon: 'mat:dashboard_customize',
            routerLinkActiveOptions: {exact: true},
            availableTo: [PermissionConstant.GENERAL_REPORT],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Employees',
        availableTo: [PermissionConstant.EMPLOYEE],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Designations',
            route: '/employees/designation',
            icon: 'mat:manage_accounts',
            availableTo: [PermissionConstant.DESIGNATION],
            is_hidden: false
          },
          {
            type: 'dropdown',
            label: 'Employees',
            icon: 'mat:admin_panel_settings',
            availableTo: [PermissionConstant.EMPLOYEES],
            is_hidden: false,
            children: [
              {
                type: 'link',
                label: 'Add',
                route: '/employees/employee/add',
                availableTo: [PermissionConstant.ADD_EMPLOYEES],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'List',
                route: '/employees/employee/list',
                availableTo: [PermissionConstant.LIST_EMPLOYEES],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Attendance',
                route: '/employees/employee/attendance',
                availableTo: [PermissionConstant.ATTENDANCE_EMPLOYEES],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Leave',
                route: '/employees/leave',
                icon: 'mat:flip_to_back',
                availableTo: [PermissionConstant.UTILITIES_HOLIDAY],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Payroll',
                route: '/employees/employee/payroll',
                availableTo: [PermissionConstant.PAYROLL_EMPLOYEES],
                is_hidden: false
              }
            ]
          },

          {
            type: 'link',
            label: 'Permissions',
            route: '/employees/permission',
            icon: 'mat:key',
            availableTo: [PermissionConstant.PERMISSION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Report',
            route: '/employees/reports',
            icon: 'mat:dashboard_customize',
            routerLinkActiveOptions: {exact: true},
            availableTo: [PermissionConstant.EMPLOYEE_REPORT],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Students',
        availableTo: [PermissionConstant.STUDENT],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Add',
            route: '/student/add',
            icon: 'mat:person_add',
            availableTo: [PermissionConstant.STUDENT_ADD],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'List Student',
            route: '/student/list',
            icon: 'mat:diversity_3',
            availableTo: [PermissionConstant.STUDENT_LIST],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Attendance',
            route: '/student/attendance',
            icon: 'mat:pending_actions',
            availableTo: [PermissionConstant.STUDENT_ADD],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Reports',
            route: '/student/report',
            icon: 'mat:dashboard_customize',
            availableTo: [PermissionConstant.STUDENT_REPORT],
            is_hidden: false
          }

        ]
      },

      {
        type: 'subheading',
        label: 'Parents',
        availableTo: [PermissionConstant.PARENT],
        is_hidden: false,
        children: [
          {
            type: 'dropdown',
            label: 'Parents',
            icon: 'mat:people',
            availableTo: [PermissionConstant.PARENT],
            is_hidden: false,
            children: [
              {
                type: 'link',
                label: 'Add',
                route: '/parents/add',
                icon: 'mat:person_add',
                availableTo: [PermissionConstant.ADD_PARENT],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'List',
                route: '/parents/list',
                icon: 'mat:format_list_bulleted',
                availableTo: [PermissionConstant.LIST_PARENT],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Report',
                route: '/reports',
                icon: 'mat:bar_chart',
                routerLinkActiveOptions: {exact: true},
                availableTo: [PermissionConstant.PARENT_REPORT],
                is_hidden: false
              }
            ]
          }
        ]
      },

      {
        type: 'subheading',
        label: 'Modules',
        availableTo: [PermissionConstant.MODULE],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Classes',
            route: '/modules/class',
            icon: 'mat:cast_for_education',
            availableTo: [PermissionConstant.CLASS],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Sections',
            route: '/modules/section',
            icon: 'mat:how_to_vote',
            availableTo: [PermissionConstant.SECTION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Subject',
            route: '/modules/subject',
            icon: 'mat:book',
            availableTo: [PermissionConstant.SUBJECT],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Subject Group',
            route: '/modules/subject-grouping',
            icon: 'mat:pin_invoke',
            availableTo: [PermissionConstant.GROUPING],
            is_hidden: false
          },

          {
            type: 'link',
            label: 'Reports',
            route: '/modules/report',
            icon: 'mat:dashboard_customize',
            availableTo: [PermissionConstant.MODULE_REPORT],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Inventorys',
        availableTo: [PermissionConstant.INVENTORY],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Categories',
            route: '/inventory/category',
            icon: 'mat:category',
            availableTo: [PermissionConstant.INVENTORY_CATEGORY],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'List',
            route: '/inventory/list',
            icon: 'mat:inventory',
            availableTo: [PermissionConstant.INVENTORY_LIST],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Transactions',
            route: '/inventory/transaction',
            icon: 'mat:receipt',
            availableTo: [PermissionConstant.INVENTORY_TRANSACTION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Reports',
            route: '/inventory/report',
            icon: 'mat:dashboard_customize',
            availableTo: [PermissionConstant.INVENTORY_REPORT],
            is_hidden: false
          }
        ]
      },


      {
        type: 'subheading',
        label: 'Books',
        availableTo: [PermissionConstant.BOOK],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Categories',
            route: '/book/category',
            icon: 'mat:category',
            availableTo: [PermissionConstant.BOOK_CATEGORY],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'List',
            route: '/book/list',
            icon: 'mat:inventory',
            availableTo: [PermissionConstant.BOOK_LIST],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Books Transactions',
            route: '/book/transaction',
            icon: 'mat:receipt',
            availableTo: [PermissionConstant.BOOK_TRANSACTION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Reports',
            route: '/book/report',
            icon: 'mat:dashboard_customize',
            availableTo: [PermissionConstant.BOOK_REPORT],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Libraries',
        availableTo: [PermissionConstant.LIBRARY],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'List',
            route: '/library/list',
            icon: 'mat:local_library',
            availableTo: [PermissionConstant.LIBRARY_BORROWING],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Borrowings',
            route: '/library/borrowing',
            icon: 'mat:library_books',
            availableTo: [PermissionConstant.LIBRARY_BORROWING],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Transactions',
            route: '/library/transaction',
            icon: 'mat:receipt',
            availableTo: [PermissionConstant.LIBRARY_TRANSACTION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Reports',
            route: '/library/report',
            icon: 'mat:dashboard_customize',
            availableTo: [PermissionConstant.LIBRARY_REPORT],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Utilities',
        availableTo: [PermissionConstant.UTILITIES],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Hostels',
            route: '/utilities/hostel',
            icon: 'mat:gite',
            availableTo: [PermissionConstant.UTILITIES_HOSTEL],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'vehicle',
            route: '/utilities/vehicle',
            icon: 'mat:gite',
            availableTo: [PermissionConstant.UTILITIES_HOSTEL],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Holidays',
            route: '/utilities/holiday',
            icon: 'mat:holiday_village',
            availableTo: [PermissionConstant.UTILITIES_HOLIDAY],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Scholarship',
            route: '/utilities/scholarship',
            icon: 'mat:emoji_events', // Better icon for scholarships and awards
            availableTo: [PermissionConstant.UTILITIES_SCHOLARSHIP],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Visitor',
            route: '/utilities/visitor',
            icon: 'mat:badge',
            availableTo: [PermissionConstant.UTILITIES_VISITOR],
            is_hidden: false
          },

          {
            type: 'link',
            label: 'Reports',
            route: '/utilities/report',
            icon: 'mat:dashboard_customize',
            availableTo: [PermissionConstant.UTILITIES_REPORT],
            is_hidden: false
          }
        ]
      },


      {
        type: 'subheading',
        label: 'Uniforms',
        availableTo: [PermissionConstant.UNIFORMS],
        is_hidden: false,
        children: [

          {
            type: 'link',
            label: 'List',
            route: '/uniform/list',
            icon: 'mat:checkroom',
            availableTo: [PermissionConstant.UNIFORMS_LIST],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Transaction',
            route: '/uniform/transaction',
            icon: 'mat:swap_horiz',
            availableTo: [PermissionConstant.UNIFORMS_TRANSACTION_LIST],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Stock',
            route: '/uniform/stock',
            icon: 'mat:inventory_2',
            availableTo: [PermissionConstant.UNIFORMS_STOCK],
            is_hidden: false
          },

        ]
      },


      {
        type: 'subheading',
        label: 'Curriculum',
        availableTo: [PermissionConstant.ALUMNI],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Alumnis',
            route: '/alumni/list',
            icon: 'mat:person',
            availableTo: [PermissionConstant.ALUMNI_LIST],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Events',
            route: '/event/list',
            icon: 'mat:emoji_events',
            availableTo: [PermissionConstant.EVENTS_LIST],
            is_hidden: false
          },

          {
            type: 'link',
            label: 'Events Participants',
            route: '/event/participant-list',
            icon: 'mat:military_tech',
            availableTo: [PermissionConstant.EVENTS_PARTICIPANTS],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Extra Curricular',
            route: '/event/extra-curricular-list',
            icon: 'mat:sports_soccer',
            availableTo: [PermissionConstant.EVENTS_EXTRA_CURRICULAR],
            is_hidden: false
          },


        ]
      },


      {
        type: 'subheading',
        label: 'Support Tickets',
        availableTo: [PermissionConstant.SUPPORT_TICKET],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Student\'s Ticket',
            route: '/support-ticket/student-support-ticket',
            icon: 'mat:confirmation_number',
            availableTo: [PermissionConstant.STUDENT_SUPPORT_TICKET],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Parent\'s Ticket',
            route: '/support-ticket/parent-support-ticket',
            icon: 'mat:confirmation_number',
            availableTo: [PermissionConstant.STUDENT_SUPPORT_TICKET],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Report',
            route: '/support-ticket/report',
            icon: 'mat:dashboard_customize',
            availableTo: [PermissionConstant.SUPPORT_TICKET_REPORT],
            is_hidden: false
          }
        ]
      },

      {
        type: 'subheading',
        label: 'Notifications',
        availableTo: [PermissionConstant.NOTIFICATION],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Student\'s Notification',
            route: '/notification/student-notification',
            icon: 'mat:notifications',
            availableTo: [PermissionConstant.STUDENT_NOTIFICATION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Parent\'s Notification',
            route: '/notification/parent-notification',
            icon: 'mat:notifications',
            availableTo: [PermissionConstant.PARENT_NOTIFICATION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Report',
            route: '/notification/report',
            icon: 'mat:dashboard_customize',
            availableTo: [PermissionConstant.NOTIFICATION_REPORT],
            is_hidden: false
          }
        ]
      }
    ]);
  }

  getSelfDetails() {
    this.authService.self().subscribe((data) => {
      if (data && data.id) {
        this.user = data;
        if (this.user && this.user['user_type'] === 'EMPLOYEE') {
          this.checkUserDetails();
        } else {
          this.loadNavigation();
        }
      }
    });
  }

  checkUserDetails() {
    this.employeeService.detail(this.user.unique_id).subscribe((data) => {
      if (data && data['employee_details'] && data['employee_details']['designation'] && data['employee_details']['designation']['designation_permissions']) {
        this.userData = data['employee_details']['designation']['designation_permissions'];
        let rolePermissions = [];
        if (this.userData && this.userData.length > 0) {
          this.userData.forEach(subPermission => {
            if (subPermission && subPermission['sub_permission'] && subPermission['sub_permission']['meta']) {
              rolePermissions.push(subPermission['sub_permission']['meta']);
            }
          });
        }
        this.loadNavigation();
        this._items.forEach(menu => {
          menu.forEach((item) => {
            if (item && item['availableTo'] && !item['availableTo'].includes('ALL')) {
              item['is_hidden'] = !Helper.findCommonElements(item['availableTo'], rolePermissions);
              if (item['children'] && item['children'].length > 0) {
                item['children'].forEach(child => {
                  child['is_hidden'] = !Helper.findCommonElements(child.availableTo, rolePermissions);
                });
              }
            } else {
              item['is_hidden'] = false;
            }
          });
        });
      }
    });
  }
}
